import React, { useEffect, useState } from "react";
import { Offer } from "interfaces/Offer";
import { getCachedConfiguration } from "utils/functions/configurationUtil";
import { TiDeviceDesktop, TiVendorAndroid } from "react-icons/ti";
import { TiVendorApple } from "react-icons/ti";
import * as c from "./SandboxPage.module.css";

declare global {
  interface Window {
    dynataOffersSdk: any;
  }
}
type FetchType = "available" | "featured" | "progress" | "banners";
type GoalMode = "none" | "extraAll" | "extraPurchaseOnly" | "extraStandardOnly";
type OsType = "Android" | "iOS" | "Desktop";

type Platform = "Android" | "iOS" | "Windows";

const platformIcons: Record<Platform, React.JSX.Element> = {
  Android: <TiVendorAndroid color="#A6D864" />,
  iOS: <TiVendorApple />,
  Windows: <TiDeviceDesktop size={14} color="#00a5ed" />,
};

const SandboxPage: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sdkVersion, setSdkVersion] = useState(() => {
    return localStorage.getItem("sandbox_sdk_version") || "2.0.1";
  });

  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [fetchType, setFetchType] = useState<FetchType>("available");
  const [goalMode, setGoalMode] = useState<GoalMode>("none");
  const [addAttributionWindows, setAddAttributionWindows] = useState(false);
  const [applyPromotion, setApplyPromotion] = useState(false);
  const [simulateCompletedPending, setSimulateCompletedPending] = useState(false);
  const [simulateRemainingTime, setSimulateRemainingTime] = useState(false);
  const [loadedVersion, setLoadedVersion] = useState<string>("");

  const [filterOs, setFilterOs] = useState<OsType[]>([]);
  const [filterLimit, setFilterLimit] = useState<number | "">("");
  const [filterOffset, setFilterOffset] = useState<number | "">("");
  const [filterIncludeGoals, setFilterIncludeGoals] = useState<boolean>(true);

  const configuration = getCachedConfiguration();

  useEffect(() => {
    loadSdkScript(sdkVersion);
    return () => {
      const existing = document.querySelector("script[data-sdk-script]");
      if (existing) existing.remove();
    };
  }, []);

  const showToast = (message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  const loadSdkScript = (version: string) => {
    const existing = document.querySelector("script[data-sdk-script]");
    if (existing) existing.remove();

    if (window.dynataOffersSdk) window.dynataOffersSdk = undefined;

    const script = document.createElement("script");

    script.src = `https://sdk.surveyb.dev/${version}/sdk.min.js?t=${Date.now()}`;
    script.async = true;
    script.dataset.sdkScript = "true";

    script.onload = () => initSdk(version);
    script.onerror = () => {
      setError(`Failed to load SDK version ${version}`);
      showToast(`❌ Failed to load SDK v${version}`, "error");
    };

    document.body.appendChild(script);
  };

  const applyNewVersion = () => {
    localStorage.setItem("sandbox_sdk_version", sdkVersion);
    window.location.reload();
  };

  async function initSdk(version: string) {
    try {
      if (!window.dynataOffersSdk) return;

      await window.dynataOffersSdk.init({
        params:
          configuration?.params ||
          "UzF4YURpTGxtYWp6YkFSZ2tXWGsxeFlOcGpZTlN5Qks3d21lMGwyLzdrR3JyOVRzNUlKZkNtdHFnM2liY3o2QllMMWYrOHowT1E3alR3Z282QlhXNkV5QUJYd1ZUcEplYVJoMS9NZ3VLWU56UTl2L3pmMlRlOVhheGg0eURJOUlWVWdrSEkyYzhTcGc5dnVxRWJpY1d6UDV6R0ZmV05TdXJPSERnZVB0am5WQkJFT0U4cUFtQjVGMmphcVFYWlplbzVNT2NpbWQvSEdPREE0NG9VSGNHdG11S2dNaklPRTE2TS9RMTFpNENNRT0",
        appUserId: configuration?.publisher_app_uid,
      });

      setLoadedVersion(version);
      loadOffers(fetchType);
      showToast(`✅ SDK v${version} loaded`, "success");
    } catch (err: any) {
      setError(err.message);
      showToast(`❌ SDK initialization failed`, "error");
    }
  }

  async function loadOffers(type: FetchType) {
    if (!window.dynataOffersSdk) return;
    setLoading(true);
    setError(null);
    setOffers([]);

    try {
      let data: Offer[] = [];

      const baseParams: any = {};
      if (filterOs.length > 0) baseParams.os = filterOs;

      const fullParams = { ...baseParams };
      if (filterLimit !== "") fullParams.limit = Number(filterLimit);
      if (filterOffset !== "") fullParams.offset = Number(filterOffset);
      fullParams.includeGoals = filterIncludeGoals;

      switch (type) {
        case "available":
          data = await window.dynataOffersSdk.fetchOffers(
            Object.keys(fullParams).length > 0 ? fullParams : undefined,
          );
          break;
        case "featured":
          data = await window.dynataOffersSdk.fetchFeaturedOffers(
            Object.keys(fullParams).length > 0 ? fullParams : undefined,
          );
          break;
        case "progress":
          data = await window.dynataOffersSdk.fetchOffersInProgress();
          break;
        case "banners":
          data = await window.dynataOffersSdk.fetchBannerAds(
            Object.keys(baseParams).length > 0 ? baseParams : undefined,
          );
          break;
        default:
          console.warn(`Unhandled fetch type: ${type}`);
          data = [];
      }
      setOffers(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const handleFetchTypeChange = (newType: FetchType) => {
    setFetchType(newType);
    setGoalMode("none");
    setAddAttributionWindows(false);
    setApplyPromotion(false);
    if (newType !== "progress") {
      setSimulateCompletedPending(false);
      setSimulateRemainingTime(false);
    }
    setOffers([]);
    setError(null);
    loadOffers(newType);
  };

  const handleGoalModeChange = (mode: GoalMode) => {
    setGoalMode((prev) => (prev === mode ? "none" : mode));
    showToast(`Goal mode: ${mode === "none" ? "off" : mode}`, "success");
  };

  const handleOsToggle = (os: OsType) => {
    setFilterOs((prev) => (prev.includes(os) ? prev.filter((item) => item !== os) : [...prev, os]));
  };

  const viewOffer = (offer: Offer, isInProgress = false) => {
    window.dynataOffersSdk.renderOfferModal(offer, isInProgress, {
      __internalGoalMode: goalMode,
      __addAttributionWindows: addAttributionWindows,
      __applyPromotionMultiplier: applyPromotion,
      __simulateCompletedPendingGoals: simulateCompletedPending,
      __simulateRemainingTime: simulateRemainingTime,
    });
  };

  return (
    <div className={c.sandboxContainer}>
      {toast && (
        <div
          className={`${c.toast} ${c.show} ${toast.type === "error" ? c.toastError : c.toastSuccess}`}
        >
          {toast.message}
        </div>
      )}

      <div className={c.testControls}>
        <div className={c.testControlsHeader}>🧪 Testing Controls</div>

        <div className={c.versionControl}>
          <label htmlFor="sdkVersion">SDK Version:</label>
          <input
            id="sdkVersion"
            className={c.versionInput}
            type="text"
            value={sdkVersion}
            onChange={(e) => setSdkVersion(e.target.value)}
          />
          <button
            className={c.applyButton}
            onClick={applyNewVersion}
            disabled={sdkVersion === loadedVersion || !sdkVersion}
            style={{
              opacity: sdkVersion === loadedVersion ? 0.5 : 1,
              cursor: sdkVersion === loadedVersion ? "not-allowed" : "pointer",
            }}
          >
            {sdkVersion === loadedVersion ? "Applied" : "Apply & Reload"}
          </button>
        </div>

        <div className={c.fetchType}>
          {(["available", "featured", "progress", "banners"] as FetchType[]).map((type) => (
            <label key={type}>
              <input
                type="radio"
                name="fetchType"
                value={type}
                checked={fetchType === type}
                onChange={() => handleFetchTypeChange(type)}
              />
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </label>
          ))}
        </div>

        {/* --- Filter Controls Section --- */}
        <div className={c.filterSection}>
          <div className={c.filterHeader}>Fetch Filters</div>

          {fetchType === "progress" ? (
            <div className={c.filterInfo}>No filters available for In Progress offers.</div>
          ) : (
            <>
              {/* OS Filter: Available for Available, Featured, and Banners */}
              <div className={c.filterRow}>
                <span style={{ fontWeight: 500 }}>OS:</span>
                {(["Android", "iOS", "Desktop"] as OsType[]).map((os) => (
                  <label key={os} className={c.filterLabel}>
                    <input
                      type="checkbox"
                      checked={filterOs.includes(os)}
                      onChange={() => handleOsToggle(os)}
                    />
                    {os}
                  </label>
                ))}
              </div>

              {/* Limit, Offset, IncludeGoals: Available ONLY for Available and Featured */}
              {(fetchType === "available" || fetchType === "featured") && (
                <div className={c.filterRow}>
                  <label className={c.filterLabel}>
                    Limit:
                    <input
                      type="number"
                      className={c.filterInput}
                      value={filterLimit}
                      onChange={(e) =>
                        setFilterLimit(e.target.value === "" ? "" : Number(e.target.value))
                      }
                      placeholder="e.g. 20"
                    />
                  </label>
                  <label className={c.filterLabel}>
                    Offset:
                    <input
                      type="number"
                      className={c.filterInput}
                      value={filterOffset}
                      onChange={(e) =>
                        setFilterOffset(e.target.value === "" ? "" : Number(e.target.value))
                      }
                      placeholder="e.g. 0"
                    />
                  </label>
                  <label className={c.filterLabel}>
                    <input
                      type="checkbox"
                      checked={filterIncludeGoals}
                      onChange={(e) => setFilterIncludeGoals(e.target.checked)}
                    />
                    Include Goals
                  </label>
                </div>
              )}

              <button
                className={`${c.applyButton} ${c.applyFiltersBtn}`}
                onClick={() => loadOffers(fetchType)}
              >
                Apply Filters & Fetch
              </button>
            </>
          )}
        </div>

        {/* Goal Mode Switches */}
        <div className={c.goalMode}>
          <div className={c.goalModeInfo}>
            ⚠️ These goal modification options are only supported in SDK version{" "}
            <strong>2.0+</strong>.
          </div>
          <span>Goal Modification Mode:</span>
          <label>
            <input
              type="checkbox"
              checked={goalMode === "extraAll"}
              onChange={() => handleGoalModeChange("extraAll")}
            />
            Add to All
          </label>
          <label>
            <input
              type="checkbox"
              checked={goalMode === "extraPurchaseOnly"}
              onChange={() => handleGoalModeChange("extraPurchaseOnly")}
            />
            Purchase Only
          </label>
          <label>
            <input
              type="checkbox"
              checked={goalMode === "extraStandardOnly"}
              onChange={() => handleGoalModeChange("extraStandardOnly")}
            />
            Standard Only
          </label>
          <div className={c.attributionInfo}>
            ℹ️ Select one of the goal modification modes above to enable attribution or promotion
            testing.
          </div>
        </div>

        {/* Attribution / Promotion Section */}
        {goalMode !== "none" && (
          <div className={c.attributionSection}>
            {/* Attribution only for Available */}
            {fetchType === "available" ? (
              <label className={c.checkboxItem}>
                <input
                  type="checkbox"
                  checked={addAttributionWindows}
                  onChange={() => setAddAttributionWindows(!addAttributionWindows)}
                />
                Add Attribution Windows (0m → 3d range)
              </label>
            ) : (
              <div className={c.attributionInfo}>
                ℹ️ Attribution windows are only available for <strong>Available</strong> offers.
              </div>
            )}

            {/* Promotion for Available + In Progress */}
            {(fetchType === "available" || fetchType === "progress") && (
              <label className={c.checkboxItem}>
                <input
                  type="checkbox"
                  checked={applyPromotion}
                  onChange={() => setApplyPromotion(!applyPromotion)}
                />
                Apply Promotion Multiplier (x2 / x3)
              </label>
            )}

            {/* Simulations — only for In Progress */}
            {fetchType === "progress" && (
              <>
                <label className={c.checkboxItem}>
                  <input
                    type="checkbox"
                    checked={simulateCompletedPending}
                    onChange={() => setSimulateCompletedPending(!simulateCompletedPending)}
                  />
                  Simulate Completed / Pending Goals (first 4)
                </label>

                <label className={c.checkboxItem}>
                  <input
                    type="checkbox"
                    checked={simulateRemainingTime}
                    onChange={() => setSimulateRemainingTime((v) => !v)}
                  />
                  Simulate Remaining Time (completeByUtc) outcomes
                </label>
              </>
            )}
          </div>
        )}
      </div>

      <h1>Offers SDK Demo</h1>

      {loading && <div className={c.loading}>Loading...</div>}
      {error && <div className={c.error}>Error: {error}</div>}

      <div className={c.offersGrid}>
        {offers.map((offer) => (
          <div
            key={offer.id}
            className={c.offerCard}
            onClick={() => viewOffer(offer, fetchType === "progress")}
          >
            <img src={offer.thumbnailUrl} alt={offer.title} className={c.offerImage} />
            <div className={c.offerContent}>
              <div className={c.offerTitle}>{offer.title}</div>
              {offer.operatingSystems !== null && offer.operatingSystems.length !== 0 && (
                <div className={c.icons}>
                  {offer.operatingSystems.map((platform) => (
                    <span key={platform}>{platformIcons[platform as Platform] || null}</span>
                  ))}
                </div>
              )}
              <div className={c.offerReward}>Earn up to {offer.rewardIac}</div>
              <div className={c.offerDetails}>
                {(offer.description || []).slice(0, 1).join(" ")}
              </div>
              <button className={c.viewButton}>View Details</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SandboxPage;
